<template>
  <div class="document-processing-indicator" :class="[`variant-${variant}`, `status-${status}`]">
    <!-- Processing -->
    <div v-if="status === 'processing'" class="processing-content">
      <q-spinner size="24px" color="primary" />
      <span v-if="showText" class="status-text">Processing...</span>
    </div>

    <!-- Pending -->
    <div v-else-if="status === 'pending'" class="pending-content">
      <q-icon name="hourglass_empty" size="24px" color="grey-6" />
      <span v-if="showText" class="status-text">Pending</span>
    </div>

    <!-- Failed -->
    <div v-else-if="status === 'failed'" class="failed-content">
      <q-icon name="error" size="24px" color="negative" />
      <span v-if="showText" class="status-text">Failed</span>
    </div>

    <!-- Completed (success) -->
    <div v-else-if="status === 'completed'" class="completed-content">
      <q-icon name="check_circle" size="24px" color="positive" />
      <span v-if="showText" class="status-text">Ready</span>
    </div>

    <!-- Additional Info -->
    <div class="additional-info" v-if="showInfo && (fileName || pageCount)">
      <div v-if="fileName" class="file-name">{{ fileName }}</div>
      <div v-if="pageCount" class="page-count">{{ pageCount }} pages</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DocumentProcessingIndicator',
  props: {
    content: {
      type: Object,
      required: true
    },
    // Add processing status prop (injected from parent like videos)
    documentProcessingStatuses: {
      type: Object,
      default: () => ({})
    },
    variant: {
      type: String,
      default: 'inline',
      validator: value => ['inline', 'overlay', 'badge'].includes(value)
    },
    showText: {
      type: Boolean,
      default: true
    },
    showInfo: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'medium', // small, medium, large
      validator: value => ['small', 'medium', 'large'].includes(value)
    }
  },
  computed: {
    status() {
      // Use proper processing status from parent, not the general document status
      const processingStatus = this.documentProcessingStatuses[this.content.id];
      if (processingStatus) {
        return processingStatus.status || 'unknown';
      }
      // Fallback to general status if no processing status available
      return this.content.status || 'unknown';
    },

    fileName() {
      return this.content.filename || this.content.name || null;
    },

    pageCount() {
      return this.content.extra_attributes?.page_count || null;
    },

    circularProgressSize() {
      const sizes = {
        small: '32px',
        medium: '42px',
        large: '56px'
      };
      return sizes[this.size] || sizes.medium;
    },

    iconSize() {
      const sizes = {
        small: '16px',
        medium: '24px',
        large: '32px'
      };
      return sizes[this.size] || sizes.medium;
    }
  }
};
</script>

<style lang="scss" scoped>
.document-processing-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  // Variant styles
  &.variant-inline {
    padding: 8px 12px;
    border-radius: 6px;
    background: var(--light-grey-hr);
  }

  &.variant-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(2px);
    border-radius: inherit;
  }

  &.variant-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 4px 8px;
    border-radius: 12px;
    background: var(--secondary-bg-color);
    border: 1px solid var(--border-color);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  // Status-specific styles
  &.status-processing {
    .processing-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .status-text {
      @include inter-font($size: 12px, $weight: 500, $color: var(--primary));
    }

    &.variant-inline {
      background: rgba(var(--primary-rgb), 0.1);
      border: 1px solid rgba(var(--primary-rgb), 0.2);
    }
  }

  &.status-pending {
    .pending-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .status-text {
      @include inter-font($size: 12px, $weight: 500, $color: var(--secondary-text-color));
    }

    &.variant-inline {
      background: rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(0, 0, 0, 0.1);
    }
  }

  &.status-failed {
    .failed-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .status-text {
      @include inter-font($size: 12px, $weight: 500, $color: var(--negative));
    }

    &.variant-inline {
      background: rgba(var(--negative-rgb), 0.1);
      border: 1px solid rgba(var(--negative-rgb), 0.2);
    }
  }

  &.status-completed {
    .completed-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .status-text {
      @include inter-font($size: 12px, $weight: 500, $color: var(--positive));
    }

    &.variant-inline {
      background: rgba(var(--positive-rgb), 0.1);
      border: 1px solid rgba(var(--positive-rgb), 0.2);
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .document-processing-indicator {
    &.variant-badge {
      top: 6px;
      right: 6px;
      padding: 3px 6px;
    }

    .status-text {
      font-size: 11px;
    }
  }
}

.additional-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-name {
  @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color));
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .variant-compact & {
    font-size: 11px;
    max-width: 150px;
  }
}

.page-count {
  @include inter-font($size: 12px, $color: var(--secondary-text-color));

  .variant-compact & {
    font-size: 10px;
  }
}

// Animation for processing states
.processing-animation {
  .status-pending &,
  .status-processing & {
    animation: pulse 2s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
